import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircleRounded,
  RadioButtonUncheckedRounded,
  WarningAmberRounded,
  AutoFixHighRounded,
} from '@mui/icons-material';
import { platformApi, type TenantDetail } from '../../../lib/api/platform.api';
import { formatDateTime, type DateLike } from '../../../lib/i18n-format';
import { TenantFlagsSection } from './TenantFlagsSection';
import { TenantModulesPanel } from '../../../components/modules/TenantModulesPanel';

export function TenantDetailDrawer({
  tenantId,
  tenantName,
  open,
  onClose,
}: {
  tenantId: string | null;
  tenantName: string;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('platform');
  const [detail, setDetail] = useState<TenantDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState<string | null>(null);

  /** El formato lo resuelve el helper desde i18n; memorizarlo acá volvía a fijar
   *  el idioma a mano. */
  const dateFmt = (v: DateLike) =>
    formatDateTime(v, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(false);
    setSeedMsg(null);
    try {
      setDetail(await platformApi.getTenantDetail(tenantId));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  /** Tras cambiar un interruptor: vuelve a leer sin tapar el panel con el spinner. */
  const refresh = useCallback(async () => {
    if (tenantId) setDetail(await platformApi.getTenantDetail(tenantId));
  }, [tenantId]);

  useEffect(() => {
    if (open && tenantId) void load();
    if (!open) setDetail(null);
  }, [open, tenantId, load]);

  async function seed() {
    if (!tenantId) return;
    setSeeding(true);
    try {
      const { applied } = await platformApi.seedStarter(tenantId);
      setSeedMsg(
        applied.length === 0
          ? t('detail.seededNone')
          : t('detail.seeded', { applied: applied.join(', ') }),
      );
      await load();
    } catch {
      setError(true);
    } finally {
      setSeeding(false);
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 440 } } }}
    >
      <Box p={3}>
        <Typography variant="h6" fontWeight={800} mb={0.5}>
          {tenantName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {tenantId}
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 3 }}>
            {t('detail.loadError')}
          </Alert>
        ) : detail ? (
          <Stack spacing={3} mt={3}>
            {/* Setup progress */}
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {t('detail.setup')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('detail.stepsDone', {
                    done: detail.setup.essentialDone,
                    total: detail.setup.essentialTotal,
                  })}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={detail.setup.pct}
                sx={{ height: 8, borderRadius: 4, mb: 1.5 }}
              />
              <Stack spacing={1}>
                {detail.setup.stages.map((stage) => (
                  <Box key={stage.key}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}
                    >
                      {t(`stages.${stage.key}`, { ns: 'setup', defaultValue: stage.key })}
                    </Typography>
                    {stage.steps.map((step) => (
                      <Stack
                        key={step.key}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        pl={0.5}
                      >
                        {step.done ? (
                          <CheckCircleRounded color="success" sx={{ fontSize: 16 }} />
                        ) : (
                          <RadioButtonUncheckedRounded
                            sx={{ fontSize: 16, color: 'text.disabled' }}
                          />
                        )}
                        <Typography
                          variant="body2"
                          color={step.done ? 'text.primary' : 'text.secondary'}
                        >
                          {t(`steps.${step.key}.title`, { ns: 'setup', defaultValue: step.key })}
                          {step.optional && (
                            <Typography component="span" variant="caption" color="text.disabled">
                              {' '}
                              · {t('detail.optional')}
                            </Typography>
                          )}
                        </Typography>
                      </Stack>
                    ))}
                  </Box>
                ))}
              </Stack>
            </Box>

            <Divider />

            {/* Health */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                {t('detail.health')}
              </Typography>
              <Stack spacing={0.75}>
                {detail.health.map((h) => (
                  <Stack key={h.key} direction="row" alignItems="center" spacing={1}>
                    {h.ok ? (
                      <CheckCircleRounded color="success" sx={{ fontSize: 16 }} />
                    ) : (
                      <WarningAmberRounded color="warning" sx={{ fontSize: 16 }} />
                    )}
                    <Typography variant="body2" color={h.ok ? 'text.primary' : 'warning.main'}>
                      {t(`health.${h.key}`, { defaultValue: h.key })}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            <Divider />

            {/* Activity */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                {t('detail.activity')}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {t('detail.lastActivity')}:{' '}
                {detail.activity.lastActivityAt
                  ? dateFmt(detail.activity.lastActivityAt)
                  : t('detail.noActivity')}
              </Typography>
              <Stack spacing={0.5}>
                {detail.activity.areas.map((a) => (
                  <Stack key={a.area} direction="row" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      {t(`areas.${a.area}`, { defaultValue: a.area })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {a.at ? dateFmt(a.at) : '—'}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            <Divider />

            {tenantId && detail.flags && (
              <TenantFlagsSection tenantId={tenantId} flags={detail.flags} onChanged={refresh} />
            )}

            <Divider />

            {/* Módulos del seminario: plantilla y ajuste fino (setTenantModules). */}
            {tenantId && <TenantModulesPanel tenantId={tenantId} platform />}

            <Divider />

            {/* Starter pack */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                {t('detail.starter')}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                {t('detail.starterHint')}
              </Typography>
              {seedMsg && (
                <Alert severity="success" sx={{ mb: 1.5 }}>
                  {seedMsg}
                </Alert>
              )}
              <Button
                variant="outlined"
                startIcon={<AutoFixHighRounded />}
                disabled={seeding}
                onClick={() => void seed()}
              >
                {t('detail.starterAction')}
              </Button>
            </Box>
          </Stack>
        ) : null}
      </Box>
    </Drawer>
  );
}
